/**
 * Drive to Store — domain types. Single source of truth for the raw DTS
 * payloads in this repo.
 *
 * Deliberately framework-free: no Angular imports, no decorators, no RxJS.
 * Two reasons.
 *
 *  1. Two consumers already exist in this repo and each used to declare its
 *     own copy: the campaign DTS tab (`../dts.service.ts`) and the reports v2
 *     Drive to Store section (`analytics/.../reportsanalytics-v2`). A drifting
 *     duplicate of `DtsTaskSummary` is exactly how a contract change starts
 *     failing in only one of the two screens.
 *  2. DTS v2 (own page) is built in `geoactivate_front_v3`, a different repo on
 *     a different Angular version with a different design system. Nothing under
 *     `core/` depends on Angular, so it ports across by copying the folder;
 *     only the presentational layer gets rewritten there.
 *
 * `../dts.service.ts` re-exports everything here, so existing
 * `from "./dts.service"` imports keep working.
 */

/**
 * The `| string` tail is deliberate: `state` is a free `varchar(40)` on the backend, not an enum, so
 * the compiler cannot promise the list is exhaustive. Everything that branches on it has to cope with
 * a value it has never seen — see `isDtsLive`.
 *
 * `canceled` is listed for completeness but the backend never emits it: zero occurrences in
 * `Modules/DriveToStore/`, and `destroy()` deletes the row instead of cancelling it.
 */
export type DtsTaskState =
    | "queued"
    | "running"
    | "completed"
    | "failed"
    | "canceled"
    | "pending_upload"
    | "downloading"
    | "computing"
    | "sending_callback"
    | "success"
    | "callback_failed"
    | "waiting_data"
    | string;

/*
 * The state vocabulary.
 *
 * One place where each backend string is given a meaning. Three lists of the same alphabet used to
 * live apart — `FAILURE_STATES` in `dts-stages.ts`, bare literals in `dts-list.vm.ts`, and
 * `DTS_TERMINAL_STATES` here — and they had already drifted: the list said `callback_failed` was a
 * campaign that did not qualify while the processing screen said its results were still available.
 * With a single vocabulary that disagreement is no longer expressible.
 *
 * Source of truth: `Modules/DriveToStore/Entities/DtsTask.php`.
 *
 *   queued → downloading → computing → sending_callback → success
 *                                                       └→ callback_failed
 *            ├──────────────────────────────────────────→ failed
 *            └──────────────────────────────────────────→ waiting_data
 */

/** The computation finished and produced a result. `completed` is a legacy alias of `success`. */
export const DTS_SUCCESS_STATES: ReadonlySet<DtsTaskState> = new Set(["success", "completed"]);

/** The computation never produced a result. See `DtsTaskState` on why `canceled` is here. */
export const DTS_FAILURE_STATES: ReadonlySet<DtsTaskState> = new Set(["failed", "canceled"]);

/**
 * The computation finished but the webhook never delivered it. **Not** a failed measurement:
 * `PollDtsTaskJob` recovers it. It currently arrives without `result`, because `ProcessDtsResultJob`
 * only stores one on `success` — which is why the list does not paint it green until the result
 * is actually there.
 */
export const DTS_AWAITING_REPORT_STATES: ReadonlySet<DtsTaskState> = new Set(["callback_failed"]);

/**
 * The measurement could not be made **yet**: the days it needs have not happened, or the provider has
 * not loaded the mobility data for them. Santiago asked (21/09) to keep this apart from a failure —
 * there is nothing to fix, only a date to come back on — so the backend leaves `errors` empty and puts
 * the actionable message in `error`.
 *
 * Terminal: the job does not retry itself, the user relaunches the measurement.
 */
export const DTS_WAITING_DATA_STATES: ReadonlySet<DtsTaskState> = new Set(["waiting_data"]);

/** A queue or a worker is going to move it. `pending_upload` and `running` are legacy. */
export const DTS_LIVE_STATES: ReadonlySet<DtsTaskState> = new Set([
    "queued",
    "pending_upload",
    "downloading",
    "computing",
    "running",
    "sending_callback",
]);

/** Terminal states — task will not transition further. */
export const DTS_TERMINAL_STATES: ReadonlySet<DtsTaskState> = new Set<DtsTaskState>([
    ...DTS_SUCCESS_STATES,
    ...DTS_FAILURE_STATES,
    ...DTS_AWAITING_REPORT_STATES,
    ...DTS_WAITING_DATA_STATES,
]);

export function isDtsTerminal(state: DtsTaskState | null | undefined): boolean {
    return state != null && DTS_TERMINAL_STATES.has(state);
}

/**
 * The measurement is still moving.
 *
 * An allow-list on purpose, and not the negation of `isDtsTerminal`: a state we do not recognise must
 * **not** count as in flight. Callers use this to decide whether launching another measurement is
 * allowed, and a blacklist meant one unknown string from the backend locked that campaign out of
 * launching anything, for good, with no way out from the UI. A duplicate measurement can be deleted;
 * a permanent block cannot.
 */
export function isDtsLive(state: DtsTaskState | null | undefined): boolean {
    return state != null && DTS_LIVE_STATES.has(state);
}

export interface DtsTaskParams {
    coste_campana: number;
    categoria: string;
    fecha_inicio_campana: string;
    fecha_fin_campana: string;
    dias_campana: number;
    /**
     * Opening campaign (model v9.3, Spain only): the store opens the day the campaign starts, so there is
     * no PRE window and no visit lift. Sent only when true.
     */
    preapertura?: boolean;
    // PRE and POST exist only in Spain: a Portugal campaign is measured without them, and an opening
    // campaign without PRE.
    fecha_inicio_pre?: string;
    fecha_fin_pre?: string;
    dias_pre?: number;
    fecha_inicio_post?: string;
    fecha_fin_post?: string;
    dias_post?: number;
    alpha?: number;
    alpha_ponderado?: number;
    n_boot?: number;
    lift_rango?: [number, number];
    top_n?: number;
    seed?: number;
    tiendas_encoding?: string;
}

export interface DtsTaskFiles {
    /**
     * Geo-audience MAIDs (one per line). Optional: when a DMP audience
     * targeted by the campaign has its MAIDs file available, the backend
     * derives it (see {@link DtsDerivableInputs.geoaudiencia}) — the
     * frontend does not upload it.
     */
    geoaudiencia?: File;
    /**
     * MediaSmart impressions. Optional: when the campaign has session-data
     * enabled, the backend extracts them from MediaSmart for the campaign
     * window — the frontend does not upload it.
     */
    mediasmart?: File;
    tiendas: File;
    /**
     * POST-window visits. Derived backend-side from {@link tiendas} + POST date range
     * via the `POST /api/v1/mobile-point` service — frontend does not upload it.
     */
    visitas?: File;
    /**
     * PRE-window visits. Derived backend-side from {@link tiendas} + PRE date range
     * via the `POST /api/v1/mobile-point` service — frontend does not upload it.
     */
    visitas_previas?: File;
}

/**
 * Which input files the backend can auto-derive for a campaign
 * (`GET /derivable-inputs`). Drives the optional/auto-derived rendering of
 * the upload slots.
 */
export interface DtsDerivableInputs {
    /** True when a targeted DMP audience has its MAIDs file available locally. */
    geoaudiencia: boolean;
    /**
     * True when a campaign is linked: the backend will attempt the MediaSmart
     * session-data export for the campaign window at the start of the job.
     * Actual data availability is only known when the export runs (the task
     * fails fast with a structured error when MediaSmart has nothing stored).
     */
    mediasmart: boolean;
    /**
     * The DMP audiences the campaign targets and whether their MAIDs can be resolved — the *why* behind
     * `geoaudiencia`, so the wizard can name the audience that has no device IDs instead of asking for
     * the file with no explanation. Absent on an API older than 30/09.
     */
    geoaudiencia_audiences?: DtsLinkedAudience[];
    /**
     * Which model will measure the campaign: `PT` when the campaign has Portugal among its countries,
     * `FR` when it has France (api #117; Portugal wins if it has both). Both are estimated from their
     * sector benchmark and only need the store catalogue and the campaign — no geo-audience,
     * impressions, visits or PRE/POST windows. Absent on an older API.
     */
    pais?: DtsCountry;
}

export type DtsCountry = "ES" | "PT" | "FR";

/** Markets measured by a model estimated from their benchmark, with only the catalogue (DtsCountry API). */
export const DTS_ESTIMATED_MARKETS: ReadonlySet<DtsCountry> = new Set<DtsCountry>(["PT", "FR"]);

/**
 * The wizard copy of each estimated market: the note on step 1, the launch chip and the windows subtitle.
 * The texts name the country, so each market has its own keys.
 */
export const DTS_MARKET_COPY: Readonly<Record<"PT" | "FR", { title: string; body: string; launch: string; windows: string }>> = {
    PT: {
        title: "dts.wizard.portugal_title",
        body: "dts.wizard.portugal_body",
        launch: "dts.wizard.launch_portugal",
        windows: "dts.wizard.windows_subtitle_pt",
    },
    FR: {
        title: "dts.wizard.france_title",
        body: "dts.wizard.france_body",
        launch: "dts.wizard.launch_france",
        windows: "dts.wizard.windows_subtitle_fr",
    },
};

/** An audience the campaign targets, as `GET /derivable-inputs` reports it. */
export interface DtsLinkedAudience {
    id: string;
    name: string | null;
    /** The geo-audience it was built from, to mark it as "used in this campaign" in the selector. */
    geo_audience_id: string | null;
    has_maids: boolean;
}

/**
 * Structured failure entry accumulated in `DtsTaskResponse.errors`: identifies
 * which pipeline stage and which service failed, and why.
 */
export interface DtsTaskError {
    /** geoaudiencia_derivation | dsp_derivation | visits_derivation | dts_submission | dts_processing | worker */
    stage: string;
    /** internal | dsp | abacus | dts. `null` for every role except super admin. */
    service: string | null;
    message: string;
    /** Technical detail (exception class, response snippet...). `null` for every role except super admin. */
    detail: string | null;
    /**
     * Whether the failure is a campaign data requirement (`true`) or a platform failure (`false`). Only
     * the derivation stages set it; absent or `null` in older tasks and in the other stages.
     */
    requirement?: boolean | null;
    /**
     * Specific reason of a requirement that needs its own copy (`dsp_retention`: the DSP no longer keeps
     * the campaign's impressions). Sent to every role, unlike `detail`. Absent in older tasks.
     */
    reason?: string | null;
    occurred_at: string;
}

export interface DtsCreateTaskResponse {
    uuid: string;
    dts_task_id: string;
    state: DtsTaskState;
    created_at: string | null;
}

export interface DtsTaskInput {
    key: string;
    filename: string;
    size_bytes: number | null;
    mime: string | null;
    derived: boolean;
    /** Signed URL served inline — consumed by the preview modal's fetch(). */
    presigned_url: string | null;
    /** Signed URL with `?download=1` → Content-Disposition attachment. Optional: absent on older API responses. */
    download_url?: string | null;
}

// =============================================================================
// DTS `result` shape
// -----------------------------------------------------------------------------
// Mirrors the JSON emitted by `reporter.dump_json()` in dts-drivetostore
// (`src/visit_lift/reporter.py`). All sub-objects are optional/nullable
// because tasks created with older DTS versions may miss fields. Render
// defensively (`*ngIf`, ?? fallbacks) instead of assuming presence.
// =============================================================================

export interface DtsResultConfig {
    categoria: string;
    coste_campana: number;
    fecha_inicio_campana: string;
    fecha_fin_campana: string;
    dias_campana: number;
    fecha_inicio_pre?: string;
    fecha_fin_pre?: string;
    dias_pre?: number;
    fecha_inicio_post?: string;
    fecha_fin_post?: string;
    dias_post?: number;
    alpha: number;
    alpha_ponderado: number;
    n_boot: number;
    top_n: number;
    seed: number;
    lift_rango?: [number, number];
}

export interface DtsResultCruce {
    n_maids: number;
    n_tratado_orig: number;
    n_control_orig: number;
    tasa_pre?: number;
    tasa_post?: number;
}

export interface DtsResultEstrato {
    /** In percent (12.58 = +12.58%), not a fraction: the model multiplies by 100. */
    lift: number;
    mde: number;
    p_valor: number;
    n_tratado: number;
    n_control: number;
    Vrt_post: number;
    Vrc_post: number;
    visitas_incrementales: number;
    significativo: boolean;
}

/** Bootstrap 95% interval of a stratum's lift. In percent, like `DtsResultEstrato.lift` (14.2 = +14.2%). */
export interface DtsResultBootEstrato {
    ic_lift_lower: number;
    ic_lift_upper: number;
    p_valor_boot: number;
}

export interface DtsResultBoot {
    /** 95% interval of the weighted lift, the report's headline figure. In percent, like `lift_ponderado_pct`. */
    ic_lift_pond_lower?: number | null;
    ic_lift_pond_upper?: number | null;
    ic_vinc_total_lower: number;
    ic_vinc_total_upper: number;
    ic_cpv_lower: number;
    ic_cpv_upper: number;
    p_valor_boot_pond: number;
    significativo_ponderado: boolean;
    estrato_a?: DtsResultBootEstrato | null;
    estrato_b?: DtsResultBootEstrato | null;
}

export interface DtsResultCheckItem {
    label: string;
    explicacion?: string | null;
    valor_str: string;
    umbral_str: string;
    grupo: string;
    estado: "ok" | "warn" | "crit";
    icono: string;
}

export interface DtsResultChecks {
    n_ok: number;
    n_warn: number;
    n_crit: number;
    lift_es_na: boolean;
    diagnostico_caso?: string | null;
    motivo_na?: string | null;
    checks: DtsResultCheckItem[];
}

export interface DtsResultNAEstimation {
    categoria_usada: string;
    bench_uplift: number;
    bench_cpv: number;
    S1_visitas: number;
    confianza_S1: number;
    S2_tamano: number;
    S2_eur: number;
    S2_dias: number;
    score: number;
    delta_pp: number;
    uplift_estimado_pct: number;
    cpv_total_est?: number;
    visitas_tot_est?: number;
    visitas_inc_est: number;
    cpv_incremental_est: number;
}

export interface DtsResultTopnRow {
    id_tienda: string | number;
    DIRECCION: string;
    uplift_pct: number;
    cpv_tienda: number;
    v_inc_asignadas: number;
    /** Present in the full breakdown (`rows`); older results / top_rows may omit them. */
    v_pre?: number;
    v_post?: number;
}

/**
 * Store excluded from the ranking (insufficient pre-campaign baseline):
 * carries raw visit counts but NO uplift/CPV attribution.
 */
export interface DtsResultStoreSinBaseline {
    id_tienda: string | number;
    DIRECCION: string;
    v_pre: number;
    v_post: number;
}

export interface DtsResultTopn {
    fuente_global: "modelo" | "NA" | string;
    uplift_global_pct?: number;
    v_inc_total_global: number;
    cpv_total_global?: number;
    n_tiendas?: number;
    n_tiendas_ranking?: number;
    n_tiendas_sin_baseline?: number;
    spread_dinamico?: number;
    modo_spread: string;
    HHI_norm?: number;
    sum_vinc_check?: number;
    top_rows: DtsResultTopnRow[];
    /** Full per-store breakdown (all ranked stores). Absent on results computed before 2026-08. */
    rows?: DtsResultTopnRow[];
    /** Stores excluded from the ranking for lack of baseline. Absent on older results. */
    rows_sin_baseline?: DtsResultStoreSinBaseline[];
}

/**
 * Global CPVs of a measured result. **The names lie in the model that runs today** (dts-service
 * `stats.py`, up to v8): `cpv_total` is coste / visitas incrementales — the *incremental* CPV — and
 * `cpv_total_bruto` is coste / visitas totales del tratado — the *total* one. The v9 port renames them
 * to `cpv_incremental` and `cpv_total`. The API resolves both contracts into the report summary
 * (`cost_per_attributed_visit`); the report never reads these keys directly.
 */
export interface DtsResultCpv {
    visitas_totales_tratado: number;
    cpv_dia: number;
    visitas_inc_dia: number;
    /** v8: incremental CPV. v9 (when `cpv_incremental` is present): total CPV. */
    cpv_total?: number;
    /** v8 only: total CPV. */
    cpv_total_bruto?: number;
    /** v9 only: incremental CPV. Its presence is what tells the two contracts apart. */
    cpv_incremental?: number;
}

/**
 * Model branch discriminator emitted by dts-service:
 *  - "lift_matematico": measured lift is reliable (normal bootstrap branch).
 *  - "lift_estimado": lift out of range, replaced by the NA benchmark estimation.
 * Optional: results computed before the field existed don't carry it.
 */
export type DtsEstadoModelo = "lift_matematico" | "lift_estimado" | "preapertura";

/**
 * The pre-opening block of the raw result (model v9.3). Super admin only: it carries the measured figures
 * next to the reported ones, which is where an estimate shows.
 */
export interface DtsResultPreapertura {
    visitas_registradas: number;
    visitantes_registrados: number;
    /** Measured: visits of people who saw the campaign (in MediaSmart). */
    visitas_impactados: number;
    visitantes_impactados: number;
    /** Measured cost per impacted visit; `null` with no impacted visits. */
    cpv_impactados: number | null;
    /** Sector cost per visit, adjusted by campaign size, and the ceiling above which the measured one is not credible. */
    cpv_sector: number;
    cpv_sector_tope: number;
    /** True when the reported impacted visits are estimated from the sector cost per visit. */
    usa_estimacion: boolean;
    visitas_impactados_informe: number;
    visitas_impactados_dia: number;
    cpv_informe: number | null;
}

export interface DtsResult {
    /** Emitted from the v9 port of the model on. Absent in older results. */
    model_version?: string;
    /** Spanish model used: absent before v9.3. */
    modo?: "estandar" | "preapertura";
    estado_modelo?: DtsEstadoModelo;
    /** Opening campaigns only (v9.3). */
    preapertura?: DtsResultPreapertura | null;
    config: DtsResultConfig;
    cruce: DtsResultCruce;
    res_a: DtsResultEstrato | null;
    res_b: DtsResultEstrato | null;
    lift_ponderado_pct: number;
    visitas_inc_total: number;
    cpv: DtsResultCpv;
    boot: DtsResultBoot | null;
    checks: DtsResultChecks;
    estimacion_na: DtsResultNAEstimation | null;
    topn: DtsResultTopn;
    [extra: string]: unknown;
}

/**
 * True when the KPIs to show come from the NA benchmark estimation instead of
 * the measured lift. Prefers the explicit `estado_modelo` discriminator; falls
 * back to inferring it from `estimacion_na` + checks for older results.
 */
export function dtsUsesNaEstimation(result: DtsResult | null | undefined): boolean {
    if (!result) return false;
    if (result.estado_modelo) {
        return result.estado_modelo === "lift_estimado" && !!result.estimacion_na;
    }
    return !!result.estimacion_na && !!result.checks?.lift_es_na;
}

/** One store of the client report: the per-store figures Appcelerate agreed to show (17/09). */
export interface DtsReportSummaryStore {
    id: string | null;
    /** `NOMBRE` from the stores file, falling back to `DIRECCION`. */
    name: string | null;
    attributed_visits: number | null;
    uplift_pct: number | null;
    /** Opening campaigns: the store's impacted visits as the report shows them. */
    impacted_visits?: number | null;
    /**
     * Cost per attributed visit of this store (model v9.1). `null` on older results, which never
     * computed it — better an empty cell than the total cost passing for the incremental one.
     */
    cost_per_attributed_visit?: number | null;
}

/**
 * What the **type of outlet** usually looks like, not what this campaign measured.
 *
 * Santiago confirmed on 30/09 that dwell and frequency come from the sector benchmark, and that on
 * the estimated branch the frequent clients are derived from incremental visits that are themselves
 * estimated. That is why the block travels whole and marked instead of mixing its numbers with the
 * campaign's: whatever renders it must say where they come from.
 */
export interface DtsSectorProfile {
    is_benchmark: true;
    /** e.g. "subcategoria 'Carnicería'" or "media del sector SUPERMERCADOS". */
    source: string | null;
    dwell: {
        median_minutes: number | null;
        bucket_minutes: number | null;
        labels: string[];
        /** Seven buckets that add up to 100. */
        pcts: (number | null)[];
    } | null;
    /**
     * `null` where the market has no figure yet — the research behind it only covers Spain — and for
     * campaigns shorter than 27 days. Never a zero: a zero would read as "nobody comes back".
     */
    frequency: {
        visits_per_month: number | null;
        frequent_clients: number | null;
    } | null;
}

/** The POST window that was really measured. */
export interface DtsReportWindow {
    start: string | null;
    end: string | null;
    /** The end the user asked for. Differs from `end` when the window was cut at the last day with data. */
    requested_end: string | null;
    /** True when the window was cut: the figures cover only part of what was asked. */
    partial: boolean;
}

/**
 * The client metrics of a measurement, resolved by the backend (`DtsReportSummary`).
 *
 * The backend decides which figure is shown — measured or estimated — and sends only this to every role
 * except super admin (Appcelerate, 17/09: the rest of the roles see the agreed metrics and nothing
 * more). Super admins also get the raw `result`, which feeds the methodology block.
 */
/**
 * How much weight the figures can take, decided by the backend on the volume of audience visits
 * (Santiago, 21/09 — by visits, not by days):
 *
 *  - `not_measurable`: under 10 audience visits. There is no report to show; Milar's 18 visits split
 *    evenly across 38 stores is what this prevents. Since model v9.4 the model itself answers that case
 *    with the sector estimate and says so in `audience_sample`: see `dtsLowSampleEstimate`.
 *  - `descriptive`: under 500. The numbers are shown as a description, not as a closed measurement.
 *  - `measured`: enough volume.
 *  - `unknown`: a result older than the `cruce` block. Shown the way it always was.
 */
export type DtsReportReliability = "measured" | "descriptive" | "not_measurable" | "unknown";

/**
 * Audience people at the stores (impacted visitors in an opening) against the model's minimum (api #116,
 * model v9.4). `null` on results older than v9.4 and in Portugal.
 */
export interface DtsAudienceSample {
    size: number;
    minimum: number;
    sufficient: boolean;
}

/**
 * Not measurable, but with figures worth showing (model v9.4, Antonio 23/10): with under 10 audience people
 * the model no longer returns a "measured" lift over a handful of devices but always the sector estimate,
 * and marks it with `sufficient = false`. Those figures are shown under the notice. A result older than
 * v9.4 with the same count carries no mark — its lift may be noise — and keeps hiding its figures.
 */
export function dtsLowSampleEstimate(
    reliability: DtsReportReliability | null | undefined,
    sampleSufficient: boolean | null | undefined
): boolean {
    return reliability === "not_measurable" && sampleSufficient === false;
}

/**
 * `opening` for an opening campaign (model v9.3): no PRE window, no visit lift. The lift metrics come
 * null and the opening ones travel in `opening`. Absent on an API older than v9.3.
 */
export type DtsReportMode = "standard" | "opening";

/** What an opening campaign reports (Santiago's pre-opening report). */
export interface DtsOpeningSummary {
    /** Visits of people who saw the campaign: measured or, when their cost is not credible, estimated. */
    impacted_visits: number | null;
    impacted_visits_per_day: number | null;
    /** Investment / impacted visits. Not the cost per attributed visit: there is no attribution. */
    cost_per_impacted_visit: number | null;
    /** Measured in the visits file; only for campaigns of 27 days or more. */
    visits_per_visitor: number | null;
}

export interface DtsReportSummary {
    mode?: DtsReportMode;
    opening?: DtsOpeningSummary;
    visit_lift_pct: number | null;
    /** The incremental visits, named the way Appcelerate reports them. */
    attributed_visits: number | null;
    /** Investment / attributed visits (the incremental CPV). */
    cost_per_attributed_visit: number | null;
    /**
     * Total visits from the model (Santiago, 09/10): measured, `cpv.visitas_totales_tratado` — the
     * denominator of the total CPV —; estimated, `estimacion_na.visitas_tot_est`. Attributed visits are
     * part of them, so they can never be larger. Absent on an API older than 09/10.
     */
    total_visits?: number | null;
    /**
     * Same value as `total_visits`, under the name the deployed front read. Until 09/10 it carried the
     * audience count instead — which is why a weekly study showed "131 total" next to "~350 attributed".
     */
    total_audience_visits: number | null;
    /**
     * Audience visits at the stores during POST (unique devices). It decides `reliability`, and since 13/10
     * the report shows it as "Identified users in store" (Antonio's proposal of 09/10, prototype v2).
     */
    audience_visits?: number | null;
    /** The control group in devices: the audience the campaign did not reach (api #75). */
    control_group_devices?: number | null;
    /** Market of the model. In Portugal the window is the campaign and there is no audience. */
    country?: DtsCountry;
    reliability: DtsReportReliability;
    /** Model v9.4 onwards (api #116); `null` before, absent on an older API. */
    audience_sample?: DtsAudienceSample | null;
    /** Model v9.1 onwards; absent on older results. */
    sector_profile?: DtsSectorProfile | null;
    stores: DtsReportSummaryStore[];
    window: DtsReportWindow;
}

export interface DtsTaskResponse {
    uuid: string;
    dts_task_id: string | null;
    state: DtsTaskState;
    campaign_id?: string | null;
    /**
     * Super admins get every param. Every other role, only the ones typed in the wizard (dates,
     * investment, vertical): enough to prefill "Launch another", none of the model settings.
     */
    params?: Record<string, unknown> | null;
    /** Super admin only. */
    inputs?: DtsTaskInput[];
    /** Client metrics, for every role. `null` until the measurement has a result. */
    summary?: DtsReportSummary | null;
    /** Raw model output. **Super admin only** — the backend does not send it to anyone else. */
    result?: DtsResult | null;
    error: string | null;
    /** Structured per-stage failures (empty when the task never failed). */
    errors?: DtsTaskError[];
    created_at?: string | null;
    started_at: string | null;
    finished_at: string | null;
}

/**
 * Fila de `GET api/v2/dts/visit-lift/tasks`.
 *
 * Todo lo que va marcado como opcional lo añadió el listado transversal de la v2, y sigue siendo
 * opcional **a propósito**: mientras el backend nuevo no esté desplegado, y para las mediciones
 * antiguas, esos campos no llegan. El núcleo tiene que construir la fila igual, sin ellos.
 */
export interface DtsTaskSummary {
    uuid: string;
    dts_task_id: string | null;
    state: DtsTaskState;
    created_at: string | null;
    started_at: string | null;
    finished_at: string | null;
    error: string | null;

    /** Nombre propio de la medición (D-DTS5). Ausente en las creadas antes de la columna. */
    name?: string | null;

    /**
     * Campaña a la que pertenece. Dentro de la pestaña sobra —la fija el contenedor—; en la página
     * propia es lo que permite agrupar y filtrar.
     */
    campaign_id?: string | null;
    campaign_name?: string | null;

    /**
     * El titular que pinta la lista, extraído del `result` en SQL.
     *
     * Existe para que la portada no tenga que pedir el detalle de cada medición terminada: con una
     * sola campaña ese N+1 salía barato, sobre toda la organización no.
     *
     * **Vienen las dos ramas del modelo.** Cuando `estado_modelo` es `lift_estimado`,
     * `lift_ponderado_pct` es justo el valor que los checks descartaron por salirse de rango, y las
     * cifras buenas son las de `estimacion_*`. Elegir cuál se enseña —y si se marca con `~`— depende
     * del rol, así que lo decide `buildMeasurementListItem`, no el backend.
     */
    estado_modelo?: DtsEstadoModelo | null;
    lift_ponderado_pct?: number | null;
    visitas_inc_total?: number | null;
    estimacion_uplift_pct?: number | null;
    estimacion_visitas_inc?: number | null;

    /**
     * El titular ya resuelto por el backend (rama medida o estimada), para todos los roles. Los cinco
     * campos de arriba solo llegan a super-admin, que los necesita para su filtro de estimadas.
     */
    visit_lift_pct?: number | null;
    attributed_visits?: number | null;

    /** `opening` en las campañas de apertura (v9.3): sin lift ni atribuidas, con visitas de impactados. */
    mode?: DtsReportMode;
    impacted_visits?: number | null;

    /**
     * La misma fiabilidad que `summary.reliability` del informe, para todos los roles (api #73). Ausente
     * con un backend anterior: la fila se queda como estaba.
     */
    reliability?: DtsReportReliability;

    /** `summary.audience_sample.sufficient` of the report (api #116): `false` = not measurable, with figures. */
    audience_sample_sufficient?: boolean | null;
}

/**
 * The measurement has a report to show. Every role gets `summary`; `result` alone covers a super admin
 * talking to a backend from before the summary existed.
 */
export function dtsHasReport(task: Pick<DtsTaskResponse, "summary" | "result"> | null | undefined): boolean {
    return !!task?.summary || !!task?.result;
}
